import { useCallback } from 'react';
import { AddressDto } from 'api/actor';
import { ReferentialKey } from 'api/referential';
import { useAppSelector } from 'app/hooks';
import { selectReferentialData } from 'features/referentials/selectors/selectReferentialDataByKey';
import { createGestionnaire, CreateGestionnaireCommand } from '../api/gestionnairesApi';
import { GestionnaireFormValues } from '../form/GestionnaireFormValues';

import { buildGerantCommand } from 'back-office/shared/components/gerant/gerantFields';
export const useGestionnaireForm = () => {
  const referentialsData = useAppSelector(selectReferentialData);

  const getReferentialId = useCallback((referentialKey: ReferentialKey, code?: string) => {
    if (!code) {
      return undefined;
    }
    return referentialsData[referentialKey].find(ref => ref.code === code)?.id;
  }, [referentialsData]);

  const buildAddressDto = useCallback((values: GestionnaireFormValues): AddressDto => ({
    line1: values.address,
    line2: values.additionalAddress || '',
    zipCode: values.postalCode,
    city: values.city,
    country: values.country,
    formattedAddress: `${values.address},${values.city},${values.country}`,
    latitude: values.latitude || undefined,
    longitude: values.longitude || undefined,
    googlePlaceId: values.googlePlaceId || undefined,
  }), []);

  const createGestionnaireFromForm = useCallback(
    async (values: GestionnaireFormValues) => {
      const plateformeIds = values.plateformeIds.flatMap(code => {
        const id = getReferentialId(ReferentialKey.Plateforme, code);
        return id ? [id] : [];
      });

      const command: CreateGestionnaireCommand = {
        companyName: values.companyName,
        address: buildAddressDto(values),
        gerant: buildGerantCommand(values, getReferentialId),
        phone: values.phone,
        registrationNumber: values.registrationNumber,
        capital: values.capital ?? 0,
        rcsCity: values.rcsCity,
        rcsNumber: values.rcsNumber,
        oriasNumber: values.oriasNumber || null,
        rcp: values.rcp || null,
        bankAccounts: (values.bankAccounts ?? []).map((row) => ({
          iban: row.iban,
          label: row.label,
          bic: row.bic || null,
          isDefault: row.isDefault,
        })),
        complaintEmail: values.complaintEmail || null,
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        username: values.username,
        password: values.password,
        plateformeIds,
      };

      return await createGestionnaire(command);
    },
    [buildAddressDto, getReferentialId]
  );

  return { createGestionnaire: createGestionnaireFromForm };
};
